import type { AutosaveState } from '@/shared/hooks/useAutosave';

const hhmm = (d: Date) => `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;

// One quiet line for a form that saves as you type. "Unsaved changes" and
// "Saving…" are shown but not announced (they would chatter on every key);
// "Saved 12:03" and "Save failed" are announced politely through role=status
// (the InfoLine pattern). 12px, muted; red only when a save failed, with
// Retry. No Save button: drafts autosave.
export function AutosaveStatus({ state, className = '' }: {
  state: Pick<AutosaveState, 'status' | 'lastSavedAt' | 'error' | 'retry'>;
  className?: string;
}) {
  const { status, lastSavedAt, error, retry } = state;
  const quiet = status === 'saving' ? 'Saving…' : status === 'pending' ? 'Unsaved changes' : '';
  const live = status === 'saved'
    ? `Saved${lastSavedAt ? ` ${hhmm(lastSavedAt)}` : ''}`
    : status === 'error' ? 'Save failed' : '';
  return (
    <span className={`inline-flex items-center gap-1.5 text-[0.75rem] whitespace-nowrap ${status === 'error' ? 'text-danger' : 'text-muted'} ${className}`}>
      {quiet && <span aria-hidden="true">{quiet}</span>}
      <span role="status" aria-live="polite" aria-atomic="true" title={status === 'error' && error ? error : undefined}>{live}</span>
      {status === 'error' && (
        <>
          {error && <span className="min-w-0 max-w-[24rem] truncate text-[0.75rem] text-danger" title={error}>{error}</span>}
          <button type="button" onClick={retry} className="rounded-sm font-medium text-primary hover:underline focus-ring">Retry</button>
        </>
      )}
    </span>
  );
}
